import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Loader } from "@/components/shared";
import { useForgotPassword } from "@/lib/react-query/queries";
import { ForgotPasswordValidation } from "@/lib/validation";
import { z } from "zod";
import { buildSignInRedirect } from "@/lib/auth/redirect";


const ForgotPassword = () => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
  
    const form = useForm({
      defaultValues: {
        emailOrPhone: ""
      },
    });
  
    const { mutateAsync: forgotPassword } = useForgotPassword();
  
    const handleForgotPassword = async (values: z.infer<typeof ForgotPasswordValidation>) => {
      setIsLoading(true);
      try {
        await forgotPassword(values.emailOrPhone);
        
        toast({
          title: "Reset link sent!",
          description: "Check your email for password reset instructions."
        });
        
        navigate(buildSignInRedirect("/home"));
      } catch (error:any) {
        toast({ 
          title: "Failed to send reset link", 
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
  
    return (
      <Form {...form}>
        <div className="sm:w-420 flex-center flex-col">
          <img src="/assets/images/logo.svg" alt="logo" />
  
          <h2 className="h3-bold md:h2-bold pt-5 sm:pt-12">
            Reset Your Password
          </h2>
          <p className="text-light-3 small-medium md:base-regular mt-2">
            Enter your email or phone to receive reset instructions
          </p>
  
          <form
            onSubmit={form.handleSubmit(handleForgotPassword)}
            className="flex flex-col gap-5 w-full mt-4">
            <FormField
              control={form.control}
              name="emailOrPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="shad-form_label">Email or Phone</FormLabel>
                  <FormControl>
                    <Input 
                      type="text" 
                      className="shad-input" 
                      placeholder="Enter your email or phone"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
  
            <Button 
              type="submit" 
              className="shad-button_primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex-center gap-2">
                  <Loader /> Sending...
                </div>
              ) : (
                "Send Reset Instructions"
              )}
            </Button>
  
            <p className="text-small-regular text-light-2 text-center mt-2">
              Remember your password?
              <Link
                to={buildSignInRedirect("/home")}
                className="text-primary-500 text-small-semibold ml-1">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </Form>
    );
  };
  
export default ForgotPassword;
